import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { LanguagesAbbr, TranslationUploadWarning, Session } from '../../../../../../../types/working-model';
import images from '../../../../../../../images';
import GradientText from "@general-ui/gradient-text/gradient-text";
import { useAppDispatch, useTypedSelector } from 'store/reducers/use-typed-selector';
import Icon, { COLORS, ICONS } from "../../../../../../general-ui/icon";
import LargeButton from '@general-ui/button/large-button';
import GradientButton from '@general-ui/button/gradient-button';
import { getLogger } from 'utils/debug-logger';
import Socket from '../../../../../../../connection/socket';
import SocketManager from '../../../../../../../connection/socket-main-thread/socket-manager';
import { commitTranslationFile, getTranslationFile, saveTranslationFile, uploadTranslationFile } from 'connection/translations';
import { blurEditor, loadWorkingEvent, lockEventSave, releaseEventSaveLock, setTranslationManifests, setWorkingEvent } from 'store/actions/admin/create-event';
import { showAlert, showAlertLong } from '@general-ui/alert/alert-service';
import WaitingIndicator from '@general-ui/waiting-indicator/waiting-indicator';
import ModalComponent from '@general-ui/modal/modal';
import { userToConnectedUser } from 'utils/utils';
import { GetSingleSession } from 'connection/sessions';
import { loadWorkingSession } from 'store/actions/admin/create-event/session';
import { streamsLoader } from 'i18n/config';
import EditorLanguageSelector from '@admin/create/translations/editor-language-selector';
import { OptionalComponent } from 'utils/optional-component';
import { availableLanguages } from '@admin/create/homepage/editor/agenda-editor/select-languages';

import './upload-translations.scss';

const logger = getLogger('bl-admin:translations');

const getIncorrectLanguageWarning = (uploadWarnings: TranslationUploadWarning[]): TranslationUploadWarning | undefined => {
	return uploadWarnings.find(warning => warning.type === 'Incorrect Language'); 
};

const rmHtml = (str: string) => {
	const div = document.createElement('div');
	div.innerHTML = str;
	return div.innerText;
};

function UploadTranslations(): JSX.Element {
	const dispatch = useAppDispatch();

	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);
	const token = useTypedSelector(state => state.AuthReducer.token);
	const user = useTypedSelector(state => state.AuthReducer.user);
	const workingSession = useTypedSelector(state => state.CreateSessionReducer.workingSession);

	const [downloading, setDownloading] = useState(false);
	const [processingUpload, setProcessingUpload] = useState(false);
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [selectedLanguage, setSelectedLanguage] = useState<LanguagesAbbr>(workingEvent?.settings.i18n?.base as LanguagesAbbr);
	const [uploadWarnings, setUploadWarnings] = useState<TranslationUploadWarning[]>([]);
	const [uploadComplete, setUploadComplete] = useState(false);
	const [applyingTranslations, setApplyingTranslations] = useState(false);

	const socket = useRef<Socket | null>(null);
	const activeChannel = useMemo(() => user?.active_channel, [user]);

	const baseLanguage = useMemo(() => workingEvent?.settings.i18n?.base, [workingEvent]);

	useEffect(() => {
		if (!selectedLanguage && baseLanguage) {
			setSelectedLanguage(baseLanguage as LanguagesAbbr);
		}
	}, [baseLanguage, selectedLanguage]);

	useEffect(() => {
		if (activeChannel && user && workingEvent) {
			socket.current = SocketManager.get(`admin-${activeChannel}`, {
				admin: userToConnectedUser(user, workingEvent.uuid)
			});
		}
	}, [dispatch, activeChannel, user, workingEvent]);

	const sessionLanguages: LanguagesAbbr[] = useMemo(() => (
		availableLanguages([workingSession as Session], undefined).map(lang => lang.code) as LanguagesAbbr[]
	), [workingSession]);

	const handleFile = async (file: File | FileList) => {
		if (file instanceof File && token && workingEvent) {
			setSelectedFile(file);
		}
	};

	const commitUpload = async () => {
		if (selectedFile && selectedLanguage && token && workingEvent) {
			try {
				setProcessingUpload(true);
				logger('Saving uploaded translations');
				if (user && socket.current) {
					socket.current.sendMessage({
						type: 'translations-updating',
						data: {
							isUpdating: true,
							admin: {
								profile_id: user.id,
								first_name: user.profile.first_name,
								last_name: user.profile.last_name
							},
							eventUuid: workingEvent.uuid
						}
					});
				}

				setUploadWarnings([]);
				setUploadComplete(false);
				const res = await uploadTranslationFile(token, selectedFile, workingEvent.uuid, selectedLanguage);
				dispatch(setTranslationManifests(res.manifests));

				setUploadWarnings(res.warnings);
				setUploadComplete(true);
			} catch (e: unknown) {
				showAlertLong({
					message: 'Unable to process upload',
					description: (e as { error: { message: string } }).error.message,
					type: 'error'
				});
			} finally {
				setProcessingUpload(false);
				if (socket.current) {
					socket.current.sendMessage({
						type: 'translations-updating',
						data: {
							isUpdating: false,
							admin: null,
							eventUuid: workingEvent.uuid
						}
					});
				}
			}
		}
	};

	const downloadFn = () => {
		if (!token || !workingEvent || downloading) return;

		setDownloading(true);
		getTranslationFile(token, workingEvent.uuid)
			.then(resp => {
				if (resp.status === 200) {
					return resp.blob();
				}
				throw ('Unable to fetch report');
			})
			.then(blob => {
				const url = window.URL.createObjectURL(blob);
				const a = document.createElement('a');
				a.style.display = 'none';
				a.href = url;
				a.download = `${workingEvent?.name}_Translations.zip`;
				document.body.appendChild(a);
				a.click();
				window.URL.revokeObjectURL(url);

				logger('Downloaded translations excel for event');
			})
			.catch((e) => {
				console.error(e);
				showAlert({ message: 'Download failed', description: 'Your translations failed to download.', duration: 3000, type: 'error' });
				console.error('Translations download failed for event: ', workingEvent.uuid);
			}).finally(() => {
				setDownloading(false);
			});
	};

	const incorrectLanguageWarning = useMemo(() => {
		const incorrectLanguage = getIncorrectLanguageWarning(uploadWarnings);

		if (incorrectLanguage) {
			const meta = incorrectLanguage.metadata as { sheet: string, key: string, value: string };

			return (
				<div className="missing-language-warning">
					<h4>Incorrect Language Detected</h4>
					<p>
						<strong className="error">
							{incorrectLanguage.type}
						</strong>
					</p>
					<div className="error-section-container">
						<section>
							<label>Sheet</label>
							<p>{meta?.sheet}</p>
						</section>

						<section>
							<label>Key</label>
							<p>{meta?.key}</p>
						</section>

						<section>
							<label>Entry</label>
							<p>{rmHtml(meta?.value)}</p>
						</section>
					</div>
				</div>
			);
		}
	}, [uploadWarnings]);

	const uploadWarningMessage = useMemo(() => {
		const missingKeyWarnings: JSX.Element[] = [];
		const missingCellWarnings: JSX.Element[] = [];

		for (const [key, value] of uploadWarnings.entries()) {
			if (value.type === 'Missing Key') {
				missingKeyWarnings.push(
					<div key={`key-warning-${key}`}>
						{value.value}
					</div>
				);
			} else if (value.type === 'Missing Cell') {
				missingCellWarnings.push(
					<div key={`cell-warning-${key}`}>
						{value.value}
					</div>
				);
			}
		}

		if (!missingKeyWarnings.length && !missingCellWarnings.length) {
			return null;
		}

		return (
			<>
				<OptionalComponent display={!!missingKeyWarnings.length}>
					<>
						<p>
							<strong className="error">
								The following rows were missing from the upload
							</strong>
						</p>
						<div className="error-section-container">
							<section>
								<p>{missingKeyWarnings}</p>
							</section>
						</div>
					</>
				</OptionalComponent>
				<OptionalComponent display={!!missingCellWarnings.length}>
					<>
						<p>
							<strong className="error">
								The following cells were missing from the upload
							</strong>
						</p>
						<div className="error-section-container">
							<section>
								<p>{missingCellWarnings}</p>
							</section>
						</div>
					</>
				</OptionalComponent>
			</>
		);
	}, [uploadWarnings]);

	const closeUploadCompleteModal = useCallback(() => {
		setUploadWarnings([]);
		setUploadComplete(false);
		setSelectedFile(null);
		setSelectedLanguage((workingEvent?.settings.i18n?.base || 'en') as LanguagesAbbr);

		if (workingEvent && token) {
			showAlert({
				message: "Fetching updated translations",
				description: "Please wait a moment while we refresh the editor.",
				duration: 5000,
			});
			dispatch(blurEditor(true));
			dispatch(loadWorkingEvent(workingEvent.uuid, token));
		}
	}, [workingEvent, token, dispatch]);

	const applyTranslations = useCallback(async () => {
		if (!token || !workingEvent) {
			return;
		}

		try {
			setApplyingTranslations(true);

			// block save requests from being sent to the server while this is processing
			dispatch(lockEventSave());

			const saved = await saveTranslationFile(token, workingEvent.uuid);
			if (!saved) {
				throw new Error(`Unable to save translation file for ${workingEvent.uuid}`);
			}

			// returns the updated event bundle
			const updatedEvent = await commitTranslationFile(token, workingEvent.uuid);

			if (!updatedEvent) {
				throw new Error(`Unable to commit translation file for ${workingEvent.uuid}`);
			}

			// force-update redux
			dispatch(setWorkingEvent(updatedEvent));

			// update workingSession
			if (workingSession) {
				const replacementWorkingSession = await GetSingleSession(token, workingSession.session);
				if (replacementWorkingSession) {
					dispatch(loadWorkingSession(replacementWorkingSession));
				}
			}

			// tell translation loader service to only use static remotes
			// and stop loading all remotes
			streamsLoader.useStaticRemote = true;
			streamsLoader.useAllRemote = false;

			closeUploadCompleteModal();
			streamsLoader.refresh();
			close();
		} catch (e) {
			console.error(e);
			showAlertLong({
				message: 'Unable to apply translations',
				description: 'We ran into an issue applying your translations. Please try again later.',
				type: 'error'
			});
		} finally {
			setApplyingTranslations(false);

			// release save lock 
			dispatch(releaseEventSaveLock());
		}
	}, [dispatch, token, workingEvent, workingSession, closeUploadCompleteModal]);

	return (
		<div className="upload-translations-container">
			<img className="pink-gradient" src={images.PinkCircularGradient} />
			<img className="telly-chair" src={images.TellyChair} />
			
			<div className="text-container">
				<GradientText>Custom Page Translations</GradientText>
				<span>Download, edit and re-upload the translations template for increased language control </span>
			</div>

			<div className="upload-button-container">
				<LargeButton
					title=""
					customTitle={
						<>
							<Icon name={ICONS.DOCUMENTS_REVERSE} color={COLORS.MAGENTA} size={14} />
							<GradientText>{selectedFile?.name ?? 'Select file'}</GradientText>
						</>
					}
					subtitle="Supported formats: .xlxs"
					onFile={handleFile}
					uploading={processingUpload}
					allowedFileTypes={['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet']}
				/>

				<OptionalComponent display={!!selectedFile}>
					<EditorLanguageSelector
						className="upload-translations-language-selector"
						handleSelectOverride={lang => setSelectedLanguage(lang)}
						languagesOverride={sessionLanguages}
						selectedLanguageOverride={selectedLanguage}
					/>
				</OptionalComponent>

				<GradientButton 
					buttonProps={{ 
						disabled: !selectedFile || processingUpload,
						onClick: commitUpload
					}}
					fullBackgroundGradient
				>
					Upload
				</GradientButton>
			</div>

			<button className="download-template-button" onClick={downloadFn} disabled={downloading}>
				{!downloading ? (
					<>
						<Icon name={ICONS.DOWNLOAD} size={16} color="white" />
						Download Template
					</>
				) : (
					<>
						<span style={{ marginRight: 6 }}>Generating file</span>
						<WaitingIndicator />
					</>
				)}
			</button>

			<ModalComponent
				title={uploadWarnings.length ? undefined : 'Your upload has finished processing'}
				className="upload-translations-post-processing-modal"
				open={uploadComplete}
				closeable
				cancellable
				onRequestClose={() => {
					setUploadWarnings([]);
					setUploadComplete(false);
				}}
				padTitle={false}
				footer={
					<div>
						<button onClick={() => {
							setUploadWarnings([]);
							setUploadComplete(false);
						}}>
							Reupload
						</button>
						<button
							className="lemonade"
							disabled={applyingTranslations}
							style={{ marginLeft: 'auto' }}
							onClick={applyTranslations}
						>
							{applyingTranslations ? (
								<WaitingIndicator />
							) : (
								'Apply Translations'
							)}
						</button>
					</div>
				}
			>
				<div>
					<OptionalComponent display={uploadComplete && !uploadWarningMessage}>
						<div className="upload-complete-message">
							<h5>Upload complete</h5>
						</div>
					</OptionalComponent>
					{incorrectLanguageWarning}
					<OptionalComponent display={!!uploadWarningMessage}>
						<div className="warning-messages">
							<h4>Missing translations</h4>
							{uploadWarningMessage}
						</div>
					</OptionalComponent>
				</div>
			</ModalComponent>
		</div>
	);
}

export default UploadTranslations;